import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { schApi, schErr } from "@/lib/schApi";

const why = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return [...(d.blocks || []), ...(d.warnings || [])][0]?.message || d.message || schErr(e);
  return schErr(e);
};

/** Unbroken runs of selected slot indexes → [[first, last], ...]. */
export const runsOf = (idx) => [...idx].sort((a, b) => a - b).reduce((out, i) => {
  const last = out[out.length - 1];
  if (last && i === last[1] + 1) last[1] = i; else out.push([i, i]);
  return out;
}, []);

/** Floating bottom-right confirm: books each unbroken run as one session between the signed-in staff member and the row's person. */
export default function SelectConfirm({ person, idx, columns, me, onClear, onDone, onBooked }) {
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null);
  const runs = runsOf(idx);
  const mins = idx.size * 30;
  const book = async () => {
    setBusy(true);
    const isRes = person.kind === "resident";
    let ok = 0;
    for (const [a, b] of runs) {
      try {
        await schApi.post("/events", {
          title: `${isRes ? "Case Management" : "Meeting"} · ${person.name}`, event_type: "case_management",
          start_time: new Date(columns[a].start).toISOString(), end_time: new Date(columns[b].end).toISOString(),
          assigned_resident_ids: isRes ? [person.id] : [], assigned_staff_ids: [...new Set([me, ...(isRes ? [] : [person.id])])],
          ...(warn ? { confirm_warnings: true } : {}),
        });
        ok += 1;
        onBooked(Array.from({ length: b - a + 1 }, (_, k) => a + k));
      } catch (e) {
        if (e?.response?.status === 409 && e.response.data?.detail?.code === "warning") { setWarn(why(e)); setBusy(false); if (ok) window.dispatchEvent(new CustomEvent("sch:changed")); return; }
        toast.error(why(e)); setBusy(false); if (ok) window.dispatchEvent(new CustomEvent("sch:changed")); return;
      }
    }
    toast.success(`Booked ${ok} session${ok === 1 ? "" : "s"} with ${person.name}`);
    window.dispatchEvent(new CustomEvent("sch:changed"));
    setBusy(false); setWarn(null); onDone();
  };
  return createPortal(
    <div data-testid="avail-select-confirm" className="fixed bottom-4 right-4 sm:bottom-20 z-[10050] flex flex-col items-end gap-1.5 max-w-[260px]">
      {warn && <p data-testid="avail-select-warning" className="text-[11px] leading-snug text-amber-100 bg-[#1a1408]/95 border border-amber-400/40 px-2.5 py-1.5 shadow-lg">{warn}</p>}
      <div className="flex items-center gap-1.5 bg-[#0b0f1a]/95 backdrop-blur border border-nex-gold/60 shadow-[0_8px_30px_rgba(0,0,0,0.5)] pl-3 pr-1 py-1">
        <span data-testid="avail-select-summary" className="text-[11px] text-nex-silver whitespace-nowrap inline-flex items-center gap-1.5">
          <span data-testid="avail-select-count" className="min-w-[18px] h-[18px] px-1 rounded-full bg-nex-gold text-black text-[10px] font-bold leading-[18px] text-center">{idx.size}</span>
          {person.name.split(" ")[0]} · {mins >= 60 ? `${mins / 60}h` : `${mins}m`}{runs.length > 1 ? ` · ${runs.length} sessions` : ""}</span>
        <button type="button" onClick={onClear} data-testid="avail-select-clear" title="Clear selection" className="p-1.5 text-nex-muted hover:text-white"><X className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={book} disabled={busy} data-testid="avail-select-book"
          className="inline-flex items-center gap-1 bg-nex-gold text-black px-3 py-1.5 text-xs font-semibold hover:brightness-110 disabled:opacity-60 transition-[filter]">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}{warn ? "Book anyway" : "Confirm"}</button>
      </div>
    </div>,
    document.body,
  );
}
